import { gql } from '@apollo/client';
import { teamIcon } from '@justgiving/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PageStatus, TeamDetailsFragment } from '../../../types/__generated__';
import config from '../../client/config';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import { getFirstTeam } from '../../utils/relationships';
import IconLabel from '../IconLabel/IconLabel';
import IsPageOwner from '../IsPageOwner/IsPageOwner';

const FRAGMENT = buildQuery(gql`
  fragment TeamDetails on Page {
    id
    legacyId
    targetWithCurrency {
      currencyCode
    }
    relationships {
      parents {
        type
        page {
          id
          legacyId
          title
          url
          status
          product {
            name
          }
          type
        }
      }
    }
  }
`);

interface TeamDetailsProps {
  showCreateTeamCTA?: boolean;
}

const TeamDetails: React.FunctionComponent<TeamDetailsProps> = ({
  showCreateTeamCTA = true,
}) => {
  const teamDetails = usePageQuery<TeamDetailsFragment>(FRAGMENT);
  const { t } = useTranslation('common', { useSuspense: false });

  const team = getFirstTeam(teamDetails.data?.relationships?.parents ?? []);
  const acceptedCurrencies = ['AUD', 'CAD', 'EUR', 'GBP', 'NZD', 'USD'];
  const isTargetCurrencyAccepted = acceptedCurrencies.includes(
    teamDetails.data?.targetWithCurrency?.currencyCode,
  );

  if (showCreateTeamCTA && isTargetCurrencyAccepted) {
    if (!UsePageQueryResult.hasData(teamDetails) || !team) {
      return (
        <IsPageOwner>
          <IconLabel
            icon={teamIcon}
            label={
              <a
                href={`${config.baseUri}/teams-creation?frpid=${teamDetails.data?.legacyId}`}
              >
                {t('page.createTeamCta')}
              </a>
            }
          />
        </IsPageOwner>
      );
    }

    if (team.status === PageStatus.Inactive) {
      return (
        <IsPageOwner>
          <IconLabel
            icon={teamIcon}
            label={
              <>
                <a
                  href={`${config.baseUri}/teams-creation/create/${team.legacyId}/team-name`}
                >
                  {t('page.finishTeamCreation')}
                </a>
                <span>: {team.title}</span>
              </>
            }
          />
        </IsPageOwner>
      );
    }
  }

  if (team) {
    return (
      <IconLabel
        icon={teamIcon}
        label={
          <span>
            Team: <a href={team.url}>{team.title}</a>
          </span>
        }
      />
    );
  }

  return null;
};

export default TeamDetails;
